/*
 * Sticky add-to-cart card for longform PDPs.
 *
 * It never owns cart state: the button proxies a click to the real
 * .product-form__submit inside the product form, and the price mirrors the
 * currently active .variant-card. That keeps variant / selling-plan handling
 * in product-single.js and product-form.js where it already lives.
 */
(function () {
  if (customElements.get('sticky-add-to-cart')) return;

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const sectionId = this.dataset.sectionId;
      this.productSection =
        document.getElementById('MainProduct-' + sectionId) || this.closest('.product-section');

      if (!this.productSection) return;

      this.trigger = this.productSection.querySelector('.product-form__buttons');
      this.submitButton = this.productSection.querySelector(
        '.product-form__buttons .product-form__submit'
      );

      if (!this.trigger || !this.submitButton) return;

      this.priceEl = this.querySelector('[data-sticky-price]');
      this.compareEl = this.querySelector('[data-sticky-compare]');
      this.ctaButton = this.querySelector('[data-sticky-cta]');
      this.ctaLabelEl = this.querySelector('[data-sticky-cta-label]');
      this.ctaPriceEl = this.querySelector('[data-sticky-cta-price]');
      this.ratingSlot = this.querySelector('[data-sticky-rating]');

      // Move to the body so a transformed or clipped ancestor can never break
      // position: fixed. Re-entry is guarded by this.initialized above.
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.cloneRating();
      this.sync();
      this.watchProductForm();
      this.bindCta();
      this.bindClose();
      this.avoidBottomWidgets();
      this.watchScroll();
    }

    /* App widgets (the Klaviyo teaser) pin themselves to the bottom of the
       viewport and cover the card on narrow screens. Lift clear of them. */
    avoidBottomWidgets() {
      const selector = '[class*="kl-teaser"], [class*="klaviyo-form"][class*="teaser"]';

      /* A widget only counts if it is actually on screen. The teaser used to be
         display: none on product pages, which made it measure zero and drop out
         of this on its own. It is now hidden by opacity and moved off-screen
         instead — display: none broke Klaviyo's close animation and left an
         invisible backdrop over the page — so it still reports a real height
         here and was lifting the bar clear of something nobody can see.

         Checked in the same terms the teaser is hidden by, plus visibility, so
         either technique is handled. */
      const isVisible = (el, rect) => {
        if (rect.height <= 0 || rect.width <= 0) return false;
        const cs = getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        if (parseFloat(cs.opacity || '1') < 0.01) return false;
        if (cs.pointerEvents === 'none') return false;
        // Translated off the bottom of the viewport: its top is past the fold.
        if (rect.top >= window.innerHeight) return false;
        return true;
      };

      this.measureBottomWidgets = () => {
        let offset = 0;

        if (window.innerWidth <= 749) {
          document.querySelectorAll(selector).forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (isVisible(el, rect) && rect.bottom > window.innerHeight - 24) {
              offset = Math.max(offset, rect.height + 8);
            }
          });
        }

        this.style.setProperty('--sticky-atc-offset', offset + 'px');
      };

      this.measureBottomWidgets();
      window.addEventListener('resize', this.measureBottomWidgets);
    }

    /* Reuse the star rating already rendered in the product info column so the
       two never drift apart. Falls back to the markup in the snippet. */
    cloneRating() {
      if (!this.ratingSlot) return;

      const source = this.productSection.querySelector('.product__title__wrapper .review-stars');
      if (!source) return;

      const clone = source.cloneNode(true);
      clone.removeAttribute('id');
      this.ratingSlot.replaceChildren(clone);
    }

    activeVariantCard() {
      return (
        this.productSection.querySelector('.custom-variant-box .variant-card.active') ||
        this.productSection.querySelector('.custom-variant-box .variant-card')
      );
    }

    sync() {
      const card = this.activeVariantCard();
      const fallback = this.dataset.fallbackPrice || '';

      let price = fallback;
      let compare = '';

      if (card) {
        const priceEl = card.querySelector('.discounted_price');
        const compareEl = card.querySelector('.original_price');
        if (priceEl && priceEl.textContent.trim()) price = priceEl.textContent.trim();
        if (compareEl) compare = compareEl.textContent.trim();
      }

      if (this.priceEl) this.priceEl.textContent = price;

      if (this.compareEl) {
        this.compareEl.textContent = compare;
        this.compareEl.hidden = compare === '' || compare === price;
      }

      const soldOut = this.submitButton.hasAttribute('disabled');

      if (this.ctaPriceEl) {
        this.ctaPriceEl.textContent = price;
        this.ctaPriceEl.hidden = soldOut || price === '';
      }

      if (this.ctaLabelEl) {
        const labelSource = this.submitButton.querySelector('span') || this.submitButton;
        const label = labelSource.textContent.trim();
        if (label) this.ctaLabelEl.textContent = label;
      }

      if (this.ctaButton) this.ctaButton.disabled = soldOut;
    }

    /* Variant selection is rewritten in place by product-single.js / global.js,
       so watch the DOM rather than listening for a specific event. */
    watchProductForm() {
      const queueSync = () => {
        if (this.syncQueued) return;
        this.syncQueued = true;
        requestAnimationFrame(() => {
          this.syncQueued = false;
          this.sync();
        });
      };

      const observer = new MutationObserver(queueSync);
      const variantBox = this.productSection.querySelector('.custom-variant-box');

      if (variantBox) {
        observer.observe(variantBox, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      observer.observe(this.submitButton, { attributes: true, childList: true, subtree: true });

      const form = this.submitButton.closest('form');
      if (form) form.addEventListener('change', queueSync);
    }

    bindCta() {
      if (!this.ctaButton) return;

      this.ctaButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.ctaButton.disabled) return;

        this.ctaButton.classList.add('sticky-atc__cta--loading');
        this.submitButton.click();

        window.setTimeout(() => {
          this.ctaButton.classList.remove('sticky-atc__cta--loading');
        }, 1500);
      });
    }

    /* Dismissal lasts for the current page view only — a reload gives the
       card another chance rather than silently hiding it for good. */
    bindClose() {
      const closeButton = this.querySelector('[data-sticky-close]');
      if (!closeButton) return;

      closeButton.addEventListener('click', () => this.dismiss());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.visible) this.dismiss();
      });
    }

    dismiss() {
      this.dismissed = true;
      this.toggle(false);
    }

    watchScroll() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Only show once the purchase options have left the top of the
            // viewport — not when they are still below the fold.
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggle(scrolledPast);
          });
        },
        { threshold: 0 }
      );

      observer.observe(this.trigger);
    }

    toggle(visible) {
      if (visible && this.dismissed) return;
      if (visible === this.visible) return;
      this.visible = visible;
      if (visible && this.measureBottomWidgets) this.measureBottomWidgets();
      this.classList.toggle('sticky-atc--visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
